// Lightweight toast notifier — no third-party dep, just a Zustand
// store + a portal-style fixed-position container. Used by the
// WebSocket subscriber (ingestion stage transitions) and any route
// that wants to surface a one-line confirmation.

import { useEffect } from "react";
import { create } from "zustand";

export type ToastKind = "info" | "success" | "error" | "warning";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: ToastKind, message: string) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (kind, message) => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, kind, message }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function pushToast(kind: ToastKind, message: string): void {
  useToastStore.getState().push(kind, message);
}

const KIND_STYLES: Record<ToastKind, string> = {
  info: "border-brand-300 bg-brand-50 text-brand-800",
  success: "border-emerald-300 bg-emerald-50 text-emerald-800",
  warning: "border-amber-300 bg-amber-50 text-amber-800",
  error: "border-red-300 bg-red-50 text-red-800",
};

export function ToastContainer(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  // Auto-dismiss after 5s. Re-running the effect when the toast list
  // changes is fine because we key the timer by id and clean up on
  // unmount.
  useEffect(() => {
    const handles = toasts.map((t) =>
      setTimeout(() => dismiss(t.id), t.kind === "error" ? 8000 : 5000),
    );
    return () => handles.forEach(clearTimeout);
  }, [toasts, dismiss]);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={`pointer-events-auto w-full max-w-md rounded-md border px-4 py-2 text-sm shadow-sm ${KIND_STYLES[t.kind]}`}
        >
          <div className="flex items-start justify-between gap-3">
            <span className="leading-snug">{t.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
              className="text-slate-400 hover:text-slate-700"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
